/**
 * No blank screens (docs/FAILURES.md): a screen that throws while drawing shows this card instead of an empty page.
 * Try again draws the screen again (its data loads again); Go home goes to the start; Tell us sends the error's
 * message and the screen's name to POST /client-errors (no PII kept beyond the user id).
 */
import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ensureStateLoaded, StateNotLoadedError } from "@logwave/core";
import { createHttpApi, type Api } from "../api.ts";
import { config } from "../env.ts";
import { useApiMaybe, useSessionMaybe } from "../session.tsx";
import { Icon } from "./Icons.tsx";
import { isChunkLoadError } from "../lib/lazy.ts";
import { crash } from "../lib/crashHook.ts";
import { StateLoadingCard } from "./StateLoading.tsx";
import { Button } from "./ui.tsx";

export const SCREEN_ERROR_TITLE = "Something went wrong on this screen. Your work is saved.";

/** A route path as a screen name, ids masked: "/people/emp_8f3k2" → "/people/:id". */
export function screenName(pathname: string): string {
  const parts = pathname.split("/").map((p) => (p.length >= 8 && (/\d/.test(p) || (p.match(/[-_]/g)?.length ?? 0) >= 2) ? ":id" : p));
  return parts.join("/") || "/";
}

/** Signed out (sign-in screen): no session client, so a bare one with no sign-in (the route allows that). */
function fallbackApi(): Pick<Api, "reportClientError"> | null {
  if (config.apiMode === "mock") return null;
  return createHttpApi({ baseUrl: config.apiUrl, auth: async () => ({}) });
}

export function ScreenErrorCard({ error, screen, onRetry }: { error: Error; screen: string; onRetry(): void }) {
  const api = useApiMaybe();
  const s = useSessionMaybe();
  const nav = useNavigate();
  const [told, setTold] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  async function tell() {
    setTold("sending");
    try {
      const client = api ?? fallbackApi();
      if (!client) throw new Error("no client");
      await client.reportClientError({ screen, message: `${error.name}: ${error.message}`.slice(0, 500), app: "web", ...(s ? { orgId: s.org.id } : {}) });
      setTold("sent");
    } catch { setTold("failed"); }
  }
  // The screen's file is gone: the app was updated while this page was open. Trying the same file again can't work; a reload fetches the new one.
  if (isChunkLoadError(error)) {
    return (
      <div className="blocked screen-error" role="alert">
        <h2><Icon name="alert" size={26} />This screen is out of date.</h2>
        <p>CleanCleaver was updated while this page was open. Reload to get the new version. Your work is saved.</p>
        <div className="row"><Button icon="undo" onClick={() => window.location.reload()}>Reload</Button></div>
      </div>
    );
  }
  return (
    <div className="blocked screen-error" role="alert">
      <h2><Icon name="alert" size={26} />{SCREEN_ERROR_TITLE}</h2>
      <p>You can try this screen again, or go home and come back.</p>
      <div className="row">
        <Button icon="undo" onClick={onRetry}>Try again</Button>
        <Button variant="secondary" icon="back" onClick={() => { onRetry(); nav("/"); }}>Go home</Button>
        <Button variant="quiet" busy={told === "sending"} disabled={told === "sent"} onClick={() => void tell()}>Tell us</Button>
      </div>
      {told === "sent" && <p className="caption" role="status">Thanks. We got it.</p>}
      {told === "failed" && <p className="caption" role="status">We couldn't send that right now. Try again later.</p>}
    </div>
  );
}

/**
 * A screen read a state's rules before they had loaded (the session loads every state of the open business first, so this
 * is a safety net): fetch that state, then draw the screen again. A failed fetch shows Try again, never a blank or other rules.
 */
function StateRecover({ error, onLoaded }: { error: StateNotLoadedError; onLoaded(): void }) {
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setFailed(false);
    ensureStateLoaded(error.state).then(() => { if (live) onLoaded(); }, () => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [error, attempt]); // eslint-disable-line react-hooks/exhaustive-deps
  return <StateLoadingCard state={error.state} status={failed ? "failed" : "loading"} onRetry={() => setAttempt((n) => n + 1)} />;
}

/** `resetKey`: when it changes (e.g. the path), a caught error is cleared and the children draw again. */
interface Props { screen: string; resetKey?: string; children: ReactNode }
interface State { error?: Error; attempt: number }

/** Catches a render error in one screen. Try again remounts the screen, so its data loads again. */
export class ScreenBoundary extends Component<Props, State> {
  state: State = { attempt: 0 };
  static getDerivedStateFromError(error: Error): Partial<State> { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error(`[screen] ${this.props.screen}: ${error.message}`, info.componentStack); if (!(error instanceof StateNotLoadedError)) crash(error); }
  componentDidUpdate(prev: Props) { if (this.state.error && prev.resetKey !== this.props.resetKey) this.retry(); }
  retry = () => this.setState((s) => ({ error: undefined, attempt: s.attempt + 1 }));
  render() {
    if (this.state.error instanceof StateNotLoadedError) return <StateRecover error={this.state.error} onLoaded={this.retry} />;
    if (this.state.error) return <ScreenErrorCard error={this.state.error} screen={this.props.screen} onRetry={this.retry} />;
    return <ScreenKey key={this.state.attempt}>{this.props.children}</ScreenKey>;
  }
}

function ScreenKey({ children }: { children: ReactNode }) { return <>{children}</>; }
